import { MultilineInput, SelectOutlined, TextInput } from '@amelio/ui-library';
import { Controller, type Control } from 'react-hook-form';

import { PLAN_FIELD_VARIANT } from './meetingPlanFieldVariant';
import { MeetingPlanSectionCard } from './MeetingPlanSectionCard';
import {
    MeetingPlanTemplateFormFields,
    OBJECTIVE_MAX_LENGTH,
    TITLE_MAX_LENGTH,
    type MeetingPlanTemplateFormData,
} from './validation';

import type { ThematicMeetingTopic } from '@/types/thematic-meetings';

import { FormField } from '@/components/FormField/FormField';
import { THEMATIC_MEETING_TOPIC_LABEL_KEYS } from '@/constants/ThematicMeetingTopic';
import { useAmelioTranslations } from '@/hooks';
import { THEMATIC_MEETING_TOPICS } from '@/types/thematic-meetings';
import { validationMessageKey } from '@/utils/validationMessage';

/** One height for the fields and the menus of the section, so they line up in its grid. */
const IDENTITY_CONTROL_SIZE = 'lg';

/** The two states of a plan, as the status menu names them; the form stores the server's flag. */
const PlanStatus = { draft: 'draft', active: 'active' } as const;

type MeetingPlanIdentitySectionProps = {
    control: Control<MeetingPlanTemplateFormData>;
    /** The conversation the plan is one way to hold. Chosen when the plan is created, shown here. */
    topic: ThematicMeetingTopic;
};

/**
 * What the plan is: the name it is listed under, the conversation it is for, whether managers are
 * offered it, what it keeps coming back to and what it is for.
 */
export const MeetingPlanIdentitySection = ({ control, topic }: MeetingPlanIdentitySectionProps) => {
    const { t } = useAmelioTranslations();
    const errorMessageOf = (message: string | undefined) =>
        message ? t(validationMessageKey(message)) : undefined;
    const topicLabel = t('templateManagement.meetingPlanEditor.topicLabel');
    const topicOptions = THEMATIC_MEETING_TOPICS.map((option) => ({
        value: option,
        label: t(THEMATIC_MEETING_TOPIC_LABEL_KEYS[option]),
    }));
    const statusLabel = t('common.templateStatus');
    // A draft first: it is the state a plan is written in before it is offered.
    const statusOptions = [
        { value: PlanStatus.draft, label: t('common.draft') },
        { value: PlanStatus.active, label: t('common.active') },
    ];

    return (
        <MeetingPlanSectionCard
            title={t('templateManagement.meetingPlanEditor.identityHeading')}
            dataTestId={'meeting-plan-template-identity'}
        >
            {/* An implicit grid is already a single column: no base `grid-cols-*` beside the
                `md:` one, which the base would win (see src/responsive-utilities.css). */}
            <div className={'grid gap-4 md:grid-cols-2'}>
                <Controller
                    control={control}
                    name={MeetingPlanTemplateFormFields.title}
                    render={({ field, fieldState }) => (
                        <FormField
                            label={t('templateManagement.meetingPlanEditor.titleLabel')}
                            required={true}
                            errorMessage={errorMessageOf(fieldState.error?.message)}
                        >
                            <TextInput
                                variant={PLAN_FIELD_VARIANT}
                                value={field.value}
                                onChange={field.onChange}
                                maxLength={TITLE_MAX_LENGTH}
                                size={IDENTITY_CONTROL_SIZE}
                                dataTestId={'meeting-plan-template-title'}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    control={control}
                    name={MeetingPlanTemplateFormFields.isActive}
                    render={({ field }) => (
                        <FormField label={statusLabel}>
                            <SelectOutlined
                                options={statusOptions}
                                value={field.value ? PlanStatus.active : PlanStatus.draft}
                                onChange={(status) => field.onChange(status === PlanStatus.active)}
                                sort={'none'}
                                size={IDENTITY_CONTROL_SIZE}
                                ariaLabel={statusLabel}
                                dataTestId={'meeting-plan-template-status'}
                            />
                        </FormField>
                    )}
                />

                {/* Shown, never changed: a meeting reads its own topic from its plan, so moving a plan
                    to another topic would rewrite every meeting already built from it. Alone on its
                    row now that Focus is gone, so it spans both columns rather than leave a hole. */}
                <div className={'md:col-span-2'}>
                    <FormField label={topicLabel}>
                        <SelectOutlined
                            options={topicOptions}
                            value={topic}
                            disabled
                            sort={'none'}
                            size={IDENTITY_CONTROL_SIZE}
                            ariaLabel={topicLabel}
                            dataTestId={'meeting-plan-template-topic'}
                        />
                    </FormField>
                </div>

                <div className={'md:col-span-2'}>
                    <Controller
                        control={control}
                        name={MeetingPlanTemplateFormFields.objective}
                        render={({ field, fieldState }) => (
                            <FormField
                                label={t('templateManagement.meetingPlanEditor.objectiveLabel')}
                                required={true}
                                errorMessage={errorMessageOf(fieldState.error?.message)}
                            >
                                <MultilineInput
                                    variant={PLAN_FIELD_VARIANT}
                                    value={field.value}
                                    onChange={field.onChange}
                                    maxLength={OBJECTIVE_MAX_LENGTH}
                                    rows={3}
                                    dataTestId={'meeting-plan-template-objective'}
                                />
                            </FormField>
                        )}
                    />
                </div>
            </div>
        </MeetingPlanSectionCard>
    );
};
